import { TRAFFIC_LEVEL_INFO, TRAFFIC_STATUS_TEXTS } from 'core';
import { ChevronRight, MessageSquareText, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { useAuth } from '@/lib/auth';
import { formatDayMonth, formatTime, romeDate } from '@/lib/time';
import { useCurrentTraffic } from '@/lib/traffic';
import type { CurrentTraffic } from '@/lib/traffic';
import { cn } from '@/lib/utils';

export interface TrafficBannerViewProps {
  /** Current level; null while the first answer is awaited or the API is unreachable. */
  status: CurrentTraffic | null;
  /** Link to the approved texts (point tablets, docs/08 §7). */
  showApprovedTexts?: boolean;
  /** The level may be old (offline, API error). */
  stale?: boolean;
  className?: string;
}

function sinceText(status: CurrentTraffic): string {
  return status.since
    ? TRAFFIC_STATUS_TEXTS.since(formatDayMonth(romeDate(status.since)), formatTime(status.since))
    : TRAFFIC_STATUS_TEXTS.noChanges;
}

/**
 * Always-visible traffic-light banner (docs/08 §7). VERDE is a thin green strip; ARANCIONE and ROSSO
 * fill the bar with the level colour and link to /semaforo (and to the approved texts).
 */
export function TrafficBannerView({
  status,
  showApprovedTexts = false,
  stale = false,
  className,
}: TrafficBannerViewProps) {
  if (!status) {
    return (
      <div
        className={cn('h-1.5 shrink-0 bg-muted', className)}
        role="status"
        aria-label="Semaforo: in caricamento"
      />
    );
  }
  const info = TRAFFIC_LEVEL_INFO[status.level];
  if (status.level === 'VERDE') {
    return (
      <Link
        to="/semaforo"
        role="status"
        aria-label={`Semaforo VERDE: ${info.sopName}`}
        className={cn(
          'flex shrink-0 items-center justify-center gap-2 bg-traffic-verde px-4 py-0.5 text-xs font-semibold text-white',
          className,
        )}
      >
        <span className="size-2 rounded-full bg-white" aria-hidden />
        SEMAFORO VERDE
        <span className="hidden font-normal opacity-90 sm:inline">
          · {info.sopName}
          {status.available ? ` · ${sinceText(status)}` : ''}
        </span>
        {stale ? <span className="font-normal opacity-90">(dato non aggiornato)</span> : null}
      </Link>
    );
  }
  const alarm = status.level === 'ROSSO' ? 'bg-traffic-rosso' : 'bg-traffic-arancione';
  return (
    <div
      role="alert"
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 py-2 text-white',
        alarm,
        className,
      )}
    >
      <Link to="/semaforo" className="flex items-center gap-2 font-bold tracking-wide">
        <TriangleAlert className="size-5" aria-hidden />
        SEMAFORO {status.level} · {info.sopName.toUpperCase()}
        <span className="font-normal opacity-90">{sinceText(status)}</span>
        {stale ? <span className="font-normal opacity-90">(dato non aggiornato)</span> : null}
        <ChevronRight className="size-4" aria-hidden />
      </Link>
      {showApprovedTexts ? (
        <Link
          to="/scheda"
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-white/20 px-3 text-sm font-semibold hover:bg-white/30"
        >
          <MessageSquareText className="size-4" aria-hidden />
          Testi approvati
        </Link>
      ) : null}
    </div>
  );
}

/** Banner wired to `GET /api/traffic/current` (refreshed by SSE, every minute as a fallback). */
export function TrafficBanner({ className }: { className?: string }) {
  const { user } = useAuth();
  const { data, isError } = useCurrentTraffic();
  return (
    <TrafficBannerView
      status={data ?? null}
      stale={isError && data != null}
      showApprovedTexts={user?.role !== 'readonly'}
      className={className}
    />
  );
}
